// Per-kind display metadata for Image Lab operations. The set of *visible*
// operations in a Studio comes from `StudioDefinition.featuredOperations`
// (spec §2.3); this catalog is the look-up table the UI uses to render each
// kind in the dock + history + panels.
//
// Replaced the v1 hardcoded `IMAGE_LAB_TOOLS` array, which has since been deleted
// along with the rest of the v1 Image Lab stack.
// The shape is intentionally close to v1's so porting the inline *Options
// components from `ImageLabStudio.tsx` into `operations/*` (F12) stays
// mechanical: same `name` / `description` / `icon` / requirement flags.

import type { ImageLabOperationType, StudioDefinition } from '../../domain';

export type OperationCategory =
  | 'Generation'
  | 'Removal'
  | 'Enhancement'
  | 'Color'
  | 'Face'
  | 'Advanced';

export interface OperationCatalogEntry {
  kind: ImageLabOperationType;
  /** Short display label used by the dock + history. */
  name: string;
  description: string;
  /** MUI icon name — looked up via `@mui/icons-material`. */
  icon: string;
  category: OperationCategory;
  requiresSourceImage: boolean;
  requiresMask: boolean;
  requiresReference: boolean;
  isPremium: boolean;
}

export const OPERATION_CATALOG: Record<ImageLabOperationType, OperationCatalogEntry> = {
  TextToImage: {
    kind: 'TextToImage',
    name: 'Generate',
    description: 'Create images from text descriptions',
    icon: 'AutoAwesome',
    category: 'Generation',
    requiresSourceImage: false,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  ImageToImage: {
    kind: 'ImageToImage',
    name: 'Transform',
    description: 'Transform images with text prompts',
    icon: 'PhotoFilter',
    category: 'Generation',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  Inpaint: {
    kind: 'Inpaint',
    name: 'Generative Fill',
    description: 'Fill masked areas with AI-generated content',
    icon: 'Brush',
    category: 'Generation',
    requiresSourceImage: true,
    requiresMask: true,
    requiresReference: false,
    isPremium: false,
  },
  Outpaint: {
    kind: 'Outpaint',
    name: 'Expand Canvas',
    description: 'Extend image beyond its borders',
    icon: 'OpenInFull',
    category: 'Generation',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  Variations: {
    kind: 'Variations',
    name: 'Variations',
    description: 'Generate similar variations of an image',
    icon: 'Difference',
    category: 'Generation',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  SketchToImage: {
    kind: 'SketchToImage',
    name: 'Sketch to Image',
    description: 'Refine a sketch into a finished image using a ControlNet',
    icon: 'Gesture',
    category: 'Generation',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  TryOn: {
    kind: 'TryOn',
    name: 'Try On',
    description: 'Composite a garment onto a model image',
    icon: 'Checkroom',
    category: 'Advanced',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: true,
    isPremium: true,
  },
  MultiAngle: {
    kind: 'MultiAngle',
    name: 'Multi-angle',
    description: 'Generate the same subject from multiple angles',
    icon: 'CameraRoll',
    category: 'Advanced',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: true,
  },
  ConsistencySheet: {
    kind: 'ConsistencySheet',
    name: 'Consistency Sheet',
    description: 'Build a grid of consistent variations for a subject',
    icon: 'GridView',
    category: 'Advanced',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: true,
  },
  RemoveBackground: {
    kind: 'RemoveBackground',
    name: 'Remove Background',
    description: 'Extract the subject with a transparent background',
    icon: 'ContentCut',
    category: 'Removal',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  RemoveObject: {
    kind: 'RemoveObject',
    name: 'Remove Object',
    description: 'Erase a masked object from the image',
    icon: 'CleaningServices',
    category: 'Removal',
    requiresSourceImage: true,
    requiresMask: true,
    requiresReference: false,
    isPremium: false,
  },
  Upscale: {
    kind: 'Upscale',
    name: 'Upscale',
    description: 'Increase image resolution',
    icon: 'ZoomIn',
    category: 'Enhancement',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  Enhance: {
    kind: 'Enhance',
    name: 'Enhance',
    description: 'Auto-enhance lighting, sharpness, and detail',
    icon: 'AutoFixHigh',
    category: 'Enhancement',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  Denoise: {
    kind: 'Denoise',
    name: 'Denoise',
    description: 'Reduce noise while preserving detail',
    icon: 'Grain',
    category: 'Enhancement',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  Deblur: {
    kind: 'Deblur',
    name: 'Deblur',
    description: 'Recover sharpness from motion or focus blur',
    icon: 'Tune',
    category: 'Enhancement',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  Zoom: {
    kind: 'Zoom',
    name: 'Zoom',
    description: 'Crop + AI-fill a focused region',
    icon: 'CenterFocusStrong',
    category: 'Enhancement',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  ColorCorrect: {
    kind: 'ColorCorrect',
    name: 'Color Correct',
    description: 'Adjust white balance, exposure, and saturation',
    icon: 'Palette',
    category: 'Color',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  ColorGrade: {
    kind: 'ColorGrade',
    name: 'Color Grade',
    description: 'Apply a cinematic color grade',
    icon: 'GradientOutlined',
    category: 'Color',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  Colorize: {
    kind: 'Colorize',
    name: 'Colorize',
    description: 'Add color to a black-and-white image',
    icon: 'InvertColors',
    category: 'Color',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  StyleTransfer: {
    kind: 'StyleTransfer',
    name: 'Style Transfer',
    description: 'Reimagine an image in the style of a reference',
    icon: 'Brush',
    category: 'Advanced',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: true,
    isPremium: false,
  },
  FaceEnhance: {
    kind: 'FaceEnhance',
    name: 'Face Enhance',
    description: 'Restore and refine faces',
    icon: 'Face',
    category: 'Face',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: false,
    isPremium: false,
  },
  FaceSwap: {
    kind: 'FaceSwap',
    name: 'Face Swap',
    description: 'Swap one face for another',
    icon: 'SwapHoriz',
    category: 'Face',
    requiresSourceImage: true,
    requiresMask: false,
    requiresReference: true,
    isPremium: true,
  },
};

/**
 * Friendly-name aliases the BE Studio seed uses for two operations whose
 * enum names are awkward (`TextToImage` / `ImageToImage`). The seed's
 * UX-facing convention is "Generate" / "Transform" — translate here so the
 * FE doesn't have to push enum names back up into the spec.
 *
 * Add new aliases here whenever the BE seed (StudioSeedData.cs) introduces
 * a friendly name that differs from `ImageLabOperationType`.
 */
const OPERATION_KIND_ALIASES: Record<string, ImageLabOperationType> = {
  Generate: 'TextToImage',
  Transform: 'ImageToImage',
};

/**
 * Resolve a single kind string (canonical or friendly alias) to the catalog
 * entry. Exposed for tests + downstream call sites that want one-shot lookup.
 */
export function resolveOperationKind(
  kind: string,
  catalog: Record<ImageLabOperationType, OperationCatalogEntry> = OPERATION_CATALOG,
): OperationCatalogEntry | undefined {
  const canonical = (OPERATION_KIND_ALIASES[kind] ?? kind) as ImageLabOperationType;
  return catalog[canonical];
}

/**
 * Resolve a Studio's `featuredOperations` (kind strings) into ordered catalog
 * entries. Unknown kinds (e.g. a Studio still referencing a kind the FE catalog
 * doesn't know about yet) are silently filtered out — the BE registry can ship
 * ahead of the FE catalog without breaking the dock. Order from the Studio is
 * preserved so curators control the dock layout from the registry.
 */
export function resolveStudioOperations(
  studio: Pick<StudioDefinition, 'featuredOperations'> | null | undefined,
  catalog: Record<ImageLabOperationType, OperationCatalogEntry> = OPERATION_CATALOG,
): OperationCatalogEntry[] {
  if (!studio) return [];
  return studio.featuredOperations
    .map(kind => resolveOperationKind(kind, catalog))
    .filter((entry): entry is OperationCatalogEntry => !!entry);
}

export const OPERATION_CATEGORIES: readonly OperationCategory[] = [
  'Generation',
  'Removal',
  'Enhancement',
  'Color',
  'Face',
  'Advanced',
];
